import { useState, useEffect, useMemo } from "react";
import { useIntegrationProviders } from "../hooks/useIntegrationProviders";
import { useIntegrationSetups } from "../../integration_setup/hooks/useIntegrationSetups";
import type IntegrationProvider from "../models/integrationProvider";
import type IntegrationSetupDto from "../../integration_setup/models/IntegrationSetup";
import { IntegrationsSidebar } from "../components/integrations_sidebar";
import { IntegrationsGrid } from "../components/integrations_grid";
import { confirmDialog } from "@/shared/components/dialog";
import GlassBackground from "@/shared/components/glassBackground";

export default function IntegrationsManagementPage() {
  const {
    loading: providersLoading,
    error: providersError,
    getAll,
    delete: deleteProvider,
    update,
  } = useIntegrationProviders();

  const {
    loading: setupsLoading,
    error: setupsError,
    getMySetups,
    test,
  } = useIntegrationSetups();

  const [integrations, setIntegrations] = useState<IntegrationProvider[]>([]);
  const [setups, setSetups] = useState<IntegrationSetupDto[]>([]);

  // Load providers
  useEffect(() => {
    let ignore = false;
    const fetch = async () => {
      const page = await getAll({ pageNumber: 1, pageSize: 100 });
      if (!ignore && page) setIntegrations(page.items);
    };
    fetch();
    return () => { ignore = true; };
  }, [getAll]);

  // Load setups
  useEffect(() => {
    let ignore = false;
    const fetch = async () => {
      const page = await getMySetups(1, 100);
      if (!ignore && page) setSetups(page.items);
    };
    fetch();
    return () => { ignore = true; };
  }, [getMySetups]);

  // Map setup by providerId for quick lookup
  const setupByProviderId = useMemo(() => {
    const map = new Map<string, IntegrationSetupDto>();
    setups.forEach((s) => map.set(s.integrationProviderId, s));
    return map;
  }, [setups]);

  // Stats
  const stats = useMemo(() => {
    const configured = integrations.filter((i) =>
      setupByProviderId.has(i.id),
    ).length;
    return {
      total: integrations.length,
      configured,
      unconfigured: integrations.length - configured,
    };
  }, [integrations, setupByProviderId]);

  const handleDeleteProvider = async (id: string) => {
    const confirmed = await confirmDialog({
      title: "Eliminar integración",
      message: "¿Eliminar este proveedor y toda su configuración? Esta acción no se puede deshacer.",
      confirmLabel: "Sí, eliminar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      await deleteProvider(id);
      setIntegrations((prev) => prev.filter((i) => i.id !== id));
      setSetups((prev) => prev.filter((s) => s.integrationProviderId !== id));
    } catch {
      // error toast handled by useAsync
    }
  };

  const handleTestSetup = async (providerId: string) => {
    const setup = setupByProviderId.get(providerId);
    if (!setup) return;
    try {
      await test(setup.id);
      const page = await getMySetups(1, 100);
      if (page) setSetups(page.items);
    } catch {
      // error toast handled by useAsync
    }
  };

  const handleToggleGlobal = async (id: string) => {
    const integration = integrations.find((i) => i.id === id);
    if (!integration) return;
    try {
      await update(id, { IsGlobal: !integration.IsGlobal });
      setIntegrations((prev) =>
        prev.map((i) => (i.id === id ? { ...i, IsGlobal: !i.IsGlobal } : i)),
      );
    } catch {
      // error toast handled by useAsync
    }
  };

  const loading = providersLoading || setupsLoading;
  const error = providersError || setupsError;

  return (
    <GlassBackground>
      <div className="relative flex w-full overflow-hidden">
        <IntegrationsSidebar
          totalIntegrations={stats.total}
          configuredCount={stats.configured}
          unconfiguredCount={stats.unconfigured}
          loading={loading}
        />

        <main className="flex-1 overflow-auto p-8">

          <IntegrationsGrid
            integrations={integrations}
            setupsMap={setupByProviderId}
            loading={loading}
            error={error}
            onDeleteProvider={handleDeleteProvider}
            onToggleGlobal={handleToggleGlobal}
            onTestSetup={handleTestSetup}
          />
        </main>
      </div>
    </GlassBackground>
  );
}
